import React, { useCallback, useMemo } from 'react'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'

import AddProductBulkTable from './AddProductBulkTable'

import { AddProductsBulkDataProvider } from '../AddProductsBulkContext'
import { usePurifiedFormik } from '../../../../../../../../hooks/formHooks/usePurifiedFormik'
import useProductsInventory from '../AddProductsBulkContext/useProductsInventory'

import { createNewProduct, getMaxOrderAndDiscount } from '../../../ProductsTable/helpers/addProduct'

import { getInitialProductDataSetup, MEDIA_PRODUCTS } from '../../../../fields'
import { isProductValid } from './AddProductBulkTable/AddProductBulkTableData/helpers'

const initialProducts = []
const AddProductBulkContent = ({ addedProducts, onProductsAdd }) => {
  const addProductsFormik = useFormik({
    initialValues: { [MEDIA_PRODUCTS]: initialProducts }
  })
  const formik = usePurifiedFormik(addProductsFormik)
  const { values, setFieldValue } = formik

  const { fetchAndSelectInventoryPeriods } = useProductsInventory(setFieldValue)

  const selectedProducts = values[MEDIA_PRODUCTS]
  const selectedValidProducts = useMemo(
    () => selectedProducts.filter(productValues => isProductValid(productValues)),
    [selectedProducts]
  )
  const lastAddedProduct = useMemo(() => addedProducts[addedProducts.length - 1], [addedProducts])
  const addedProductsIds = useMemo(() => addedProducts.map(product => product.data.id), [addedProducts])

  const onSelectProducts = useCallback(
    (newSelectedProducts, dateRange) => {
      const { maxOrder, discount } = getMaxOrderAndDiscount(addedProducts)

      // remove products which are already in the list
      const prevSelectedProductsIds = selectedProducts.map(product => product.data.id)
      const onlyNewProducts = newSelectedProducts.filter(product => !prevSelectedProductsIds.includes(product.id))

      const newFormattedProducts = onlyNewProducts.map((productData, productIndex) => {
        // here the product is fetched and we add it data
        const productValues = createNewProduct(maxOrder + productIndex + 1, discount)
        const productFormattedData = getInitialProductDataSetup(productData)

        return {
          ...productValues,
          ...productFormattedData,
          isInventoryLoading: true
        }
      })

      setFieldValue(MEDIA_PRODUCTS, [...selectedProducts, ...newFormattedProducts])

      // to create a correct product path we need to consider new product index which starts from the end of the selected products
      const startIndex = selectedProducts.length
      // fetch inventory data and preselect periods for selected products and date range
      fetchAndSelectInventoryPeriods(newFormattedProducts, dateRange, startIndex)
    },
    [addedProducts, fetchAndSelectInventoryPeriods, selectedProducts, setFieldValue]
  )

  const handleFiltersChange = useCallback(() => {
    // reset selected products when filters are changed
    setFieldValue(MEDIA_PRODUCTS, [])
  }, [setFieldValue])

  return (
    <AddProductsBulkDataProvider
      addedProductsIds={addedProductsIds}
      lastAddedProduct={lastAddedProduct}
      onFiltersChange={handleFiltersChange}
      selectedValidProducts={selectedValidProducts}
    >
      <AddProductBulkTable formik={formik} onProductsAdd={onProductsAdd} onSelectProducts={onSelectProducts} />
    </AddProductsBulkDataProvider>
  )
}

AddProductBulkContent.propTypes = {
  addedProducts: PropTypes.array,
  onProductsAdd: PropTypes.func.isRequired
}

export default AddProductBulkContent
